/**
 * a small sample of fabric intended to demonstrate the look of a larger piece, paint, makeup, etc. applied to a surface or one skin to test or demonstrate the color.
 *
 * @module views/component/ProductSwatches
 * @memberof -Common
 */
import './ProductSwatches.scss';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import { useToggle } from '@ulta/core/hooks/useToggle/useToggle';
import { hasItems } from '@ulta/core/utils/array/array';

import Tag from '@ulta/modules/Tag/Tag';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import Swatch from '../Swatch/Swatch';

const VISIBLE_SWATCHES_SIZE = 45;

/**
 * Represents a ProductSwatches component
 *
 * @method
 * @param {ProductSwatchesProps} props - React properties passed from composition
 * @returns ProductSwatches
 */
export const ProductSwatches = function( props ){
  const [expanded, toggleExpanded] = useToggle( props.expanded );

  const swatchRef = useRef( null );
  const {
    variants: variantsProps,
    viewLessLabel,
    viewAllLabel,
    viewAllAction,
    width = defaultProps.width,
    height = defaultProps.height
  } = props;

  const variants = hasItems( variantsProps ) ? variantsProps : [];
  // Todo need to add collapsible once dxl chnages done
  const showButton = variants.length > VISIBLE_SWATCHES_SIZE;

  /* Sets the UUID for the swatch buttons */
  const [swatchUuid] = useUuid();


  return (
    <div className='ProductSwatches'>
      <div ref={ swatchRef } className={ classNames( 'ProductSwatches--content' ) }>
        <ul>
          { variants.map( ( item, index ) => {
            /* Accessibility (a11y): Generate a globally unique, collision-proof ID using the rail's instance UUID and item index. */
            const tagsId = item.tags?.length > 0 ? `${swatchUuid}-pill-tags-${index}` : undefined;
            return (
              <li key={ `productSwatch:${index}` }
                className={ classNames( {
                  'hidden': !expanded && ( index > VISIBLE_SWATCHES_SIZE - 1 ),
                  'hasTags': hasItems( item.tags )
                } ) }
              >
                <Swatch
                  key={ index }
                  src={ item.swatchImage?.imageUrl }
                  alt={ item.swatchImage?.description }
                  outofStockLabel={ item.outofStockLabel }
                  selected={ item.selected }
                  disabled={ item.disabled }
                  variantUrl={ item.variantUrl }
                  unavailable={ item.unavailable }
                  showIcon={ true }
                  width={ width }
                  height={ height }
                  selectAction={ item.selectAction }
                  linkSelectAction={ item.linkSelectAction }
                  onSwatchClick={ () => {
                    props.selectAction( item );
                  } }
                  withHoverAndSelected={ true }
                  ariaDescribedBy={ tagsId } /* Link the button/link to the tag description */
                />
                { item.tags?.length > 0 && (
                  <div id={ tagsId } className='ProductSwatches__tags'>
                    { item.tags.map( ( tag, tagIndex ) => (
                      <Tag
                        key={ `${tagIndex}-${tag.message}` }
                        boldText={ tag.message }
                        size={ tag.size }
                        variant={ tag.variant }
                        backgroundColor={ tag.backgroundColor }
                        textColor={ tag.textColor }
                      />
                    ) ) }
                  </div>
                ) }
              </li>
            );
          } )
          }
          { showButton && !expanded && (
            <li>
              <Button
                likeLink
                action={ viewAllAction }
                onClick={ toggleExpanded }
                label={ `+${variants.length - VISIBLE_SWATCHES_SIZE}` }
                ariaLabel={ `${viewAllLabel} ${
                  variants.length - VISIBLE_SWATCHES_SIZE
                }` }
              />
            </li>
          ) }
          { showButton && expanded && (
            <li>
              <Button
                onClick={ toggleExpanded }
                action={ viewAllAction }
                likeLink
                label={ viewLessLabel }
                ariaLabel={ viewLessLabel }
              />
            </li>
          ) }
        </ul>
      </div>
    </div>
  );
};

/**
 * Property type definitions
 *
 * @typedef ProductSwatchesProps
 * @type {object}
 * @property {array} variants - This is the list of product received from backend for a specific query.
 * @property {boolean} expanded - Hold the value if filter is in expanded state at given time.
 * @property {boolean} collapsible - This sets the behavior of component that if it is collapsible or not.
 * @property {string} viewLessLabel - This is label for collapse button.
 * @property {string} viewAllLabel - This is aria-label for expand button.
 * @property {number} width - This is the width of swatch.
 * @property {number} height - This is the height of swatch.
 */

export const propTypes = {
  /** This is the list of product received from backend for a specific query.*/
  variants: PropTypes.arrayOf(
    PropTypes.shape( {
      productId: PropTypes.string,
      skuId: PropTypes.string,
      name: PropTypes.string,
      shadeDescription: PropTypes.string,
      swatchImage: PropTypes.shape( {
        imageUrl: PropTypes.string,
        description: PropTypes.string
      } ),
      smooshImage: PropTypes.shape( {
        imageUrl: PropTypes.string,
        description: PropTypes.string
      } ),
      selected: PropTypes.bool,
      disabled: PropTypes.bool,
      unavailable: PropTypes.bool,
      listPrice: PropTypes.string,
      salePrice: PropTypes.string,
      selectAction: PropTypes.oneOfType( [PropTypes.object, PropTypes.func] )
    } )
  ).isRequired,
  /** Hold the value if filter is in expanded state at given time.*/
  expanded: PropTypes.bool,
  /** This sets the behavior of component that if it is collapsible or not.*/
  collapsible: PropTypes.bool,
  /** This is label for collapse button.*/
  viewLessLabel: PropTypes.string,
  /** This is aria-label for expand button.*/
  viewAllLabel: PropTypes.string,
  /** This is the width of swatch.*/
  width: PropTypes.number,
  /** This is the height of swatch.*/
  height: PropTypes.number
};

/**
 * Default values for passed properties
 * @type object
 * @property {number} [ width = 40 ] - by default width is 40
 * @property {number} [ height = 40 ] - by default height is 40
 */
export const defaultProps = {
  width: 40,
  height: 40
};

ProductSwatches.propTypes = propTypes;
ProductSwatches.defaultProps = defaultProps;

export default ProductSwatches;
